import {inject, Injectable} from '@angular/core';
import {OrderFormDraftDto} from '../../models/order-form-draft';
import {FormSaverPersistenceService} from './form-saver-persistence.service';
import {OrderFormFactoryService} from './order-form-factory.service';

export type BookingDraftRoute =
  | '/select-property'
  | '/service-provider'
  | '/property-owner'
  | '/power-of-attorney'
  | '/provider-power-of-attorney'
  | '/inheritance-documents'
  | '/checkout';

const bookingDraftRouteSteps: Record<BookingDraftRoute, number> = {
  '/select-property': 1,
  '/service-provider': 2,
  '/property-owner': 3,
  '/power-of-attorney': 4,
  '/provider-power-of-attorney': 5,
  '/inheritance-documents': 6,
  '/checkout': 7,
};

@Injectable({providedIn: 'root'})
export class FormSaverFacadeService {
  private readonly orderFormFactory = inject(OrderFormFactoryService);
  private readonly persistence = inject(FormSaverPersistenceService);
  private localDraftRestored = false;

  /**
   * Returns the current order form value as the draft that should be persisted.
   *
   * @returns Current order form draft.
   */
  public getDraft(): OrderFormDraftDto {
    return this.orderFormFactory.orderForm.getRawValue() as OrderFormDraftDto;
  }

  /**
   * Persists the current order form draft in local storage.
   */
  public saveLocal(): void {
    this.persistence.persistLocal(this.getDraft());
  }

  /**
   * Persists a draft locally and restores it into the central order form.
   *
   * @param draft Order form draft returned by the backend.
   */
  public saveAndRestoreLocal(draft: OrderFormDraftDto): void {
    this.persistence.persistLocal(draft);
    this.restoreLocal();
  }

  /**
   * Clears the locally stored order form draft.
   */
  public clearLocal(): void {
    this.persistence.clearLocal();
  }

  /**
   * Clears the persisted draft and resets the active in-memory booking form.
   */
  public clearLocalAndReset(): void {
    this.clearLocal();
    this.orderFormFactory.reset();
  }

  /**
   * Restores a locally stored draft into the central order form.
   *
   * @returns The restored draft, or `null` when no local draft exists.
   */
  public restoreLocal(): OrderFormDraftDto | null {
    this.localDraftRestored = true;
    const restored = this.persistence.restore();
    if (!restored) {
      return null;
    }

    const restoredForm = this.orderFormFactory.create(restored);
    this.orderFormFactory.orderForm.reset(restoredForm.getRawValue(), {emitEvent: false});
    this.orderFormFactory.updatePowerOfAttorneyInformation(restored.powerOfAttorneyInformation);
    this.orderFormFactory.updateEntryParameters(restored.entryParameters ?? {});
    this.orderFormFactory.refreshObjectOwnerValidators();
    if (restored.property) {
      this.orderFormFactory.updateProperty(restored.property);
    } else {
      this.orderFormFactory.clearProperty();
    }
    this.orderFormFactory.syncDerivedState();

    return restored;
  }

  /**
   * Restores the local draft only once for the current app session.
   *
   * Route guards and booking step components can all run during the same
   * navigation flow. This method prevents later navigations from resetting the
   * in-memory form back to the persisted draft after the user has already edited
   * the current step.
   *
   * @returns The restored draft on the first call, or the current in-memory draft afterwards.
   */
  public restoreLocalOnce(): OrderFormDraftDto | null {
    if (this.localDraftRestored) {
      return this.getDraft();
    }

    return this.restoreLocal();
  }

  private logInvalidControls(): void {

    const form = this.orderFormFactory.userForm;

    Object.entries(form.controls).forEach(([controlName, control]) => {

      if (control.invalid) {

        console.log(`Invalid control: ${controlName}`, control.errors);

      }

    });

  }

  /**
   * Finds the first booking route whose form requirements are not complete yet.
   *
   * @returns Route that should be opened next in the booking flow.
   */
  public getFirstInvalidBookingRoute(): BookingDraftRoute {
    this.orderFormFactory.refreshObjectOwnerValidators();
    this.orderFormFactory.refreshPowerOfAttorneyValidators();

    if (!this.hasSelectedProperty()) {
      return '/select-property';
    }

    if (this.orderFormFactory.userForm.invalid || !this.orderFormFactory.isCurrentUserMailVerified()) {
      return '/service-provider';
    }

    if (this.orderFormFactory.objectOwnerForm.invalid) {
      return '/property-owner';
    }

    const powerOfAttorneyControls = this.orderFormFactory.powerOfAttorneyInformationForm.controls;
    if (powerOfAttorneyControls.isPowerOfAttorneyAvailable.invalid || powerOfAttorneyControls.uploadedAuthorityTemporaryUploadIds.invalid) {
      return '/power-of-attorney';
    }

    if (!powerOfAttorneyControls.submissionId.value) {
      return '/provider-power-of-attorney';
    }

    if (this.orderFormFactory.objectOwnerForm.controls.isInheritanceCase.value === true && powerOfAttorneyControls.uploadedInheritanceTemporaryUploadIds.invalid) {
      return '/inheritance-documents';
    }

    return '/checkout';
  }

  /**
   * Calculates the farthest step the user may open from the restored draft.
   *
   * @param includeInheritanceDocumentationStep Whether the visible stepper contains the inheritance step.
   * @returns One-based step index relative to the current visible stepper.
   */
  public getHighestReachableBookingStep(includeInheritanceDocumentationStep: boolean): number {
    const route = this.getFirstInvalidBookingRoute();

    if (route === '/checkout' && !includeInheritanceDocumentationStep) {
      return 6;
    }

    return bookingDraftRouteSteps[route];
  }

  /**
   * Returns the one-based step index for a booking route.
   *
   * @param route Booking route to resolve.
   * @returns Step index used by the booking stepper.
   */
  public getBookingRouteStep(route: BookingDraftRoute): number {
    return bookingDraftRouteSteps[route];
  }

  /**
   * Checks whether the current draft contains an inheritance documentation step.
   *
   * @returns `true` when the inheritance document step belongs to the current flow.
   */
  public hasInheritanceDocumentationStep(): boolean {
    return this.orderFormFactory.objectOwnerForm.controls.isInheritanceCase.value === true;
  }

  /**
   * Checks whether the draft contains at least one selected parcel.
   *
   * @returns `true` when the property selection step is complete.
   */
  private hasSelectedProperty(): boolean {
    const property = this.orderFormFactory.property();

    return (
      property?.landregisterfolios.some((folio) =>
        folio.flure.some((flur) => flur.flurstuecke.length > 0),
      ) === true
    );
  }
}
